import { DialogTitle } from "@headlessui/react";
import { Tag } from "lucide-react";
import { useEffect, useState } from "react";

import { useFetcher, useSearchParams } from "react-router";

import type { EventFragment } from "~/__generated__/graphql/graphql";

import { PrimaryButton } from "~/components/Button";
import { StepIndicator } from "~/components/plans/components/StepIndicator";
import { Text } from "~/components/Text";
import { useRootLocale } from "~/root";
import type { TicketQuoteResult } from "~/routes/api.events.$eventId.ticket-quote";
import { centsToMoney } from "~/utils/money";
import { CheckoutFooter } from "./CheckoutFooter";

import { useCheckoutSession } from "./CheckoutSession";
import { DiscountCodeInput } from "./DiscountCodeInput";

import { TicketVariationSelector } from "./TicketVariationSelector";
import {
  getSelectedTicketVariation,
  TICKET_VARIATION_PARAM,
} from "./ticketVariations";

type Props = {
  plan: EventFragment;
  ticketVariationsEnabled: boolean;
  nextStep?: string;
};

export function CheckoutTicketsStep({
  plan,
  ticketVariationsEnabled,
  nextStep = "details",
}: Props) {
  const locale = useRootLocale();
  const checkout = useCheckoutSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialVariation = ticketVariationsEnabled
    ? getSelectedTicketVariation(plan, searchParams.get(TICKET_VARIATION_PARAM))
    : undefined;
  const [selectedVariationID, setSelectedVariationID] = useState<string | null>(
    initialVariation?.id ?? null,
  );
  const [isContinuing, setIsContinuing] = useState(false);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);

  const discountCode = searchParams.get("discountCode")?.trim() ?? "";
  const selectedVariation = ticketVariationsEnabled
    ? getSelectedTicketVariation(plan, selectedVariationID)
    : undefined;
  const ticketVariationID = selectedVariation?.id;
  const hasTicket = !ticketVariationsEnabled || !!selectedVariation;

  const quoteFetcher = useFetcher<TicketQuoteResult>({
    key: `ticket-quote:${plan.id}`,
  });

  const quoteParams = new URLSearchParams();
  if (ticketVariationID) {
    quoteParams.set(TICKET_VARIATION_PARAM, ticketVariationID);
  }
  if (discountCode) quoteParams.set("discountCode", discountCode);
  const quoteURL = `/api/events/${plan.id}/ticket-quote?${quoteParams}`;
  const loadQuote = quoteFetcher.load;

  useEffect(() => {
    if (!hasTicket) return;
    loadQuote(quoteURL);
  }, [hasTicket, loadQuote, quoteURL]);

  const quoteData = quoteFetcher.data;
  const quoteMatchesSelection =
    quoteData !== undefined &&
    quoteData.ticketVariationID === (ticketVariationID ?? null) &&
    quoteData.discountCode === discountCode;
  const quote = quoteMatchesSelection && quoteData.ok ? quoteData.ticketCost : null;
  const quoteError =
    quoteFetcher.state === "idle" && quoteMatchesSelection
      ? quoteData.error
      : null;
  const quoteIsLoading =
    hasTicket &&
    (quoteFetcher.state !== "idle" ||
      !quoteMatchesSelection ||
      (!quote && !quoteError));

  const handleContinue = () => {
    if (!quote?.available || quoteIsLoading || isApplyingDiscount) return;
    setIsContinuing(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setSearchParams(
          (previous) => {
            const next = new URLSearchParams(previous);
            if (selectedVariation) {
              next.set(TICKET_VARIATION_PARAM, selectedVariation.id);
            } else {
              next.delete(TICKET_VARIATION_PARAM);
            }
            next.set("step", nextStep);
            return next;
          },
          { replace: true, preventScrollReset: true },
        );
      });
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col sm:block">
      <div className="shrink-0">
        <StepIndicator currentStep={1} showTicketStep />
      </div>

      <div className="-mx-6 mt-[10px] min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-[30px] pb-2 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-6">
        <Text as={DialogTitle} variant="primary">
          Choose your ticket
        </Text>
        <div className="mt-2.5">
          {ticketVariationsEnabled ? (
            <TicketVariationSelector
              plan={plan}
              enabled
              selectedVariationID={selectedVariationID}
              onSelect={(id) => {
                setSelectedVariationID(id);
                checkout.update({ ticketVariationID: id, paymentPhase: "overview" });
                setSearchParams(previous => { const next = new URLSearchParams(previous); next.set(TICKET_VARIATION_PARAM, id); return next; }, { replace: true, preventScrollReset: true });
              }}
            />
          ) : (
            <section aria-label="Your ticket">
              <div className="flex w-full items-center gap-3 rounded-[9px] border border-butter-green bg-butter-green px-3 py-2.5 text-left text-butter-cream-light">
                <span
                  aria-hidden
                  className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-butter-cream text-xl"
                >
                  🎟️
                </span>
                <span className="min-w-0 flex-1 font-sans text-base font-bold">
                  Full access
                </span>
                {typeof plan.regularTicketCost?.totalCostCents === "number" && (
                  <span className="shrink-0 font-sans text-base font-bold">
                    {centsToMoney(
                      plan.regularTicketCost.totalCostCents,
                      "$",
                      locale,
                    )}
                  </span>
                )}
              </div>
            </section>
          )}
        </div>

        {!hasTicket && (
          <Text as="p" variant="secondary" size="sm" className="mt-3">
            Choose an available ticket to continue.
          </Text>
        )}


        {hasTicket && (
          <section className="mt-5" aria-labelledby="your-bag-heading">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <Text
                id="your-bag-heading"
                as="h2"
                variant="secondary"
                size="md"
                weight="bold"
              >
                Your bag
              </Text>
              <div aria-live="polite" aria-busy={quoteIsLoading}>
                {quoteIsLoading ? (
                  <span
                    role="status"
                    aria-label="Loading ticket price"
                    className="block h-5 w-24 animate-pulse rounded bg-butter-cream"
                  />
                ) : quote ? (
                  <Text as="span" variant="secondary" size="md" weight="bold">
                    {centsToMoney(quote.totalCostCents, "$", locale)}{" "}
                    <span className="text-sm font-normal">incl. fees</span>
                  </Text>
                ) : null}
              </div>
            </div>

            <DiscountCodeInput
              eventId={plan.id}
              eventTitle={plan.title}
              appliedCode={discountCode}
              onApplyingChange={setIsApplyingDiscount}
            />

            {!quoteIsLoading && quote && quote.totalDiscountCents > 0 && (
              <div className="mt-4 flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-4 font-sans text-base text-butter-green">
                  <span>Sub-total</span>
                  <span>
                    {centsToMoney(
                      quote.originalTotalCents ??
                        quote.totalCostCents + quote.totalDiscountCents,
                      "$",
                      locale,
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4 font-sans text-base text-butter-green">
                  <span className="inline-flex items-center gap-1.5">
                    Discount
                    <Tag className="size-3.5" aria-hidden />
                  </span>
                  <span>{centsToMoney(quote.totalDiscountCents, "$", locale)}</span>
                </div>
                <div className="flex items-center justify-between gap-4 font-sans text-base font-bold text-butter-green">
                  <span>Total</span>
                  <span className="inline-flex items-center gap-2">
                    {quote.originalTotalCents !== null && (
                      <span className="font-normal line-through opacity-60">
                        {centsToMoney(quote.originalTotalCents, "$", locale)}
                      </span>
                    )}
                    <span>{centsToMoney(quote.totalCostCents, "$", locale)}</span>
                  </span>
                </div>
                <p className="text-right font-sans text-sm font-normal text-butter-lime">
                  You&apos;re saving{" "}
                  {centsToMoney(quote.totalDiscountCents, "$", locale)}
                </p>
              </div>
            )}

            {quoteError && (
              <div className="mt-4 font-sans text-sm text-butter-pink">
                <p role="alert">{quoteError}</p>
                <button
                  type="button"
                  className="mt-1 font-bold text-butter-green underline"
                  onClick={() => loadQuote(quoteURL)}
                >
                  Try again
                </button>

              </div>
            )}
          </section>
        )}
      </div>

      <CheckoutFooter showTerms={false}>
        <PrimaryButton
          type="button"
          className="text-base!"
          loading={isContinuing}
          disabled={
            isContinuing ||
            !hasTicket ||
            !quote?.available ||
            quoteIsLoading ||
            isApplyingDiscount
          }
          onClick={handleContinue}
        >
          Next
        </PrimaryButton>


      </CheckoutFooter>
    </div>
  );
}
